import { RefObject, useCallback, useEffect, useRef } from 'react';
import type { ProgrammaticScrollGuard } from 'src/pages/Conversations/Chat/registry/use-programmatic-scroll-guard';
import type { ThreadGestureClock } from 'src/pages/Conversations/Chat/registry/use-thread-gesture-clock';

// rows re-measure their collapse 100ms after mount (chat-message.tsx), rich content and images later
export const ANCHOR_SETTLE_MS = 600;
export const MAX_ANCHOR_MS = 5000;
export const MAX_ROW_WAIT_FRAMES = 30;

/*
  Centres a message row and keeps it centred while the thread's rows settle their height: a
  freshly merged page shrinks as long messages fold behind Show More after the first paint,
  which would carry the row thousands of pixels off screen. Lets go on the reader's first
  gesture or click, once the thread stops resizing, or at the cap.
*/
export function useCenteredRowAnchor(
  chatRef: RefObject<HTMLDivElement | null>,
  scrollGuard: ProgrammaticScrollGuard,
  gestureClock: ThreadGestureClock,
): (messageId: string) => void {
  const releaseRef = useRef<(() => void) | null>(null);

  useEffect(() => () => releaseRef.current?.(), []);

  const holdCentered = useCallback(
    (container: HTMLElement, row: Element) => {
      const center = (): void =>
        scrollGuard.run(() => row.scrollIntoView({ block: 'center', behavior: 'instant' }));
      center();

      let settleTimer: ReturnType<typeof setTimeout> | undefined;

      const observer = new ResizeObserver(() => {
        if (!row.isConnected) {
          release();
          return;
        }
        center();
        clearTimeout(settleTimer);
        settleTimer = setTimeout(release, ANCHOR_SETTLE_MS);
      });
      const capTimer = setTimeout(release, MAX_ANCHOR_MS);
      const unsubscribe = gestureClock.subscribe(release);

      function release(): void {
        observer.disconnect();
        clearTimeout(settleTimer);
        clearTimeout(capTimer);
        unsubscribe();
        document.removeEventListener('click', release, true);
        if (releaseRef.current === release) releaseRef.current = null;
      }

      Array.from(container.children).forEach((child) => observer.observe(child));
      settleTimer = setTimeout(release, ANCHOR_SETTLE_MS);
      // capture: a Show More click must not be answered by a jump back to the row
      document.addEventListener('click', release, true);
      releaseRef.current = release;
    },
    [scrollGuard, gestureClock],
  );

  return useCallback(
    (messageId: string) => {
      releaseRef.current?.();

      /*
        The thread swaps its skeleton for the rows a render after the target resolves
        (context-selector consumers update in a pass of their own), so the row may not be
        in the DOM yet when this is called.
      */
      let frame: number | undefined;
      const cancelWait = (): void => {
        if (frame !== undefined) cancelAnimationFrame(frame);
        if (releaseRef.current === cancelWait) releaseRef.current = null;
      };

      const findRow = (attempt: number): void => {
        const container = chatRef.current;
        const row = container?.querySelector(`[data-message-id="${CSS.escape(messageId)}"]`);
        if (container && row) {
          if (releaseRef.current === cancelWait) releaseRef.current = null;
          holdCentered(container, row);
          return;
        }
        if (attempt < MAX_ROW_WAIT_FRAMES) {
          frame = requestAnimationFrame(() => findRow(attempt + 1));
        }
      };

      releaseRef.current = cancelWait;
      findRow(0);
    },
    [chatRef, holdCentered],
  );
}
